(() => {
  const panelSelector = '[data-b2b-form-panel]';
  const controllers = new Map();

  const getControlledTrigger = (panelId) => {
    if (!panelId) return null;

    if (window.CSS?.escape) {
      return document.querySelector(`[aria-controls="${window.CSS.escape(panelId)}"]`);
    }

    return document.querySelector(`[aria-controls="${panelId}"]`);
  };

  const copyText = (text) => {
    if (!text) {
      return Promise.resolve();
    }

    if (navigator.clipboard?.writeText) {
      return navigator.clipboard.writeText(text);
    }

    return new Promise((resolve, reject) => {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();

      try {
        document.execCommand('copy');
        resolve();
      } catch (error) {
        reject(error);
      } finally {
        textarea.remove();
      }
    });
  };

  const destroyPanel = (panel) => {
    const controller = controllers.get(panel);
    if (!controller) return;

    controller.cleanup();
    controllers.delete(panel);
  };

  const initPanel = (panel) => {
    if (!panel || controllers.has(panel)) return;

    const panelId = panel.getAttribute('id');
    const trigger = getControlledTrigger(panelId);
    const inner = panel.querySelector('[data-open-on-load]');

    if (!panelId || !trigger || !inner) return;

    const cleanupFns = [];
    const resetTimers = new Map();
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const addListener = (target, eventName, handler, options) => {
      if (!target) return;

      target.addEventListener(eventName, handler, options);
      cleanupFns.push(() => target.removeEventListener(eventName, handler, options));
    };

    const openText = trigger.getAttribute('data-open-text') || 'Open form';
    const closeText = trigger.getAttribute('data-close-text') || 'Close form';
    const successState = panel.querySelector('.gm-b2b-registration-success');

    const setState = (isOpen) => {
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      trigger.textContent = isOpen ? closeText : openText;
      panel.classList.toggle('is-hidden', !isOpen);
    };

    const openPanel = (scrollToPanel) => {
      setState(true);

      if (!scrollToPanel) return;

      window.setTimeout(() => {
        panel.scrollIntoView({
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
          block: 'start'
        });
      }, 120);
    };

    const closePanel = () => {
      setState(false);
    };

    addListener(trigger, 'click', () => {
      if (panel.classList.contains('is-hidden')) {
        openPanel(true);
      } else {
        closePanel();
      }
    });

    if (inner.getAttribute('data-open-on-load') === 'true') {
      openPanel(false);
      successState?.focus?.();
    } else {
      closePanel();
    }

    panel.querySelectorAll('[data-coupon-target]').forEach((button) => {
      const handleCopy = () => {
        const targetId = button.getAttribute('data-coupon-target');
        const coupon = targetId ? document.getElementById(targetId) : null;
        if (!coupon) return;

        const originalText = button.textContent;
        const successText = button.getAttribute('data-copy-success-label') || originalText;
        const label = button.getAttribute('data-copy-label') || originalText;

        copyText(coupon.textContent.trim())
          .then(() => {
            button.textContent = successText;
            button.setAttribute('aria-label', successText);

            const currentTimer = resetTimers.get(button);
            if (currentTimer) window.clearTimeout(currentTimer);

            const timeoutId = window.setTimeout(() => {
              button.textContent = originalText || label;
              button.setAttribute('aria-label', label);
              resetTimers.delete(button);
            }, 1400);

            resetTimers.set(button, timeoutId);
          })
          .catch((error) => {
            console.error('[B2BFacility] Failed to copy coupon code:', error);
          });
      };

      addListener(button, 'click', handleCopy);
    });

    controllers.set(panel, {
      cleanup() {
        cleanupFns.forEach((cleanup) => cleanup());
        cleanupFns.length = 0;

        resetTimers.forEach((timeoutId) => window.clearTimeout(timeoutId));
        resetTimers.clear();
      }
    });
  };

  const queryPanels = (scope = document) => {
    if (!scope) return [];
    return scope.matches?.(panelSelector) ? [scope] : Array.from(scope.querySelectorAll(panelSelector));
  };

  const initAll = (scope = document) => {
    queryPanels(scope).forEach(initPanel);
  };

  const destroyAll = (scope = document) => {
    queryPanels(scope).forEach(destroyPanel);
  };

  if (document.readyState !== 'loading') {
    initAll();
  } else {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
})();
